import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Badge,
  IconButton,
  Popover,
  Box,
  Typography,
  Divider,
  Button,
  Stack,
  Chip,
  Tooltip,
} from "@mui/material";
import NotificationsIcon from "@mui/icons-material/Notifications";
import {
  getNotifications,
  getUnreadNotificationCount,
  markNotificationRead,
  markAllNotificationsRead,
} from "api/notificationsApi";
import { unwrapList, unwrapObject } from "api/unwrap";

const LEVEL_COLOR = {
  info: "#0ea5e9",
  warning: "#f97316",
  error: "#dc2626",
};

const fmtAge = (ms) => {
  if (!ms) return "";
  const diff = Date.now() - ms;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
};

export const NotificationBell = () => {
  const navigate = useNavigate();
  const [anchor, setAnchor] = useState(null);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);

  const refreshCount = useCallback(async () => {
    try {
      const res = await getUnreadNotificationCount();
      const obj = unwrapObject(res);
      setUnread(obj?.count ?? 0);
    } catch {
      /* silent — bell just won't update */
    }
  }, []);

  useEffect(() => {
    refreshCount();
    const t = setInterval(refreshCount, 60_000);
    return () => clearInterval(t);
  }, [refreshCount]);

  const open = async (e) => {
    setAnchor(e.currentTarget);
    try {
      const res = await getNotifications();
      setItems(unwrapList(res));
    } catch {
      /* silent */
    }
  };

  const onRowClick = async (n) => {
    if (!n.isRead) {
      try {
        await markNotificationRead(n.id);
      } catch { /* silent */ }
    }
    setAnchor(null);
    if (n.linkUrl) navigate(n.linkUrl);
    refreshCount();
  };

  const onMarkAll = async () => {
    try {
      await markAllNotificationsRead();
      setItems((prev) => prev.map((n) => ({ ...n, isRead: true })));
      setUnread(0);
    } catch { /* silent */ }
  };

  return (
    <>
      <Tooltip title="Notifications">
        <IconButton size="small" onClick={open}>
          <Badge badgeContent={unread} color="error" max={99}>
            <NotificationsIcon />
          </Badge>
        </IconButton>
      </Tooltip>
      <Popover
        open={!!anchor}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { sx: { width: 380, maxHeight: 480 } } }}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", p: 1.5 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Notifications</Typography>
          {unread > 0 ? (
            <Button size="small" onClick={onMarkAll}>Mark all read</Button>
          ) : null}
        </Box>
        <Divider />
        {items.length === 0 ? (
          <Box sx={{ p: 3, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">No notifications yet.</Typography>
          </Box>
        ) : (
          <Stack divider={<Divider />}>
            {items.map((n) => (
              <Box
                key={n.id}
                onClick={() => onRowClick(n)}
                sx={{
                  p: 1.5,
                  cursor: n.linkUrl ? "pointer" : "default",
                  backgroundColor: n.isRead ? "#ffffff" : "#f1f5f9",
                  borderLeft: `3px solid ${LEVEL_COLOR[n.level] || "#94a3b8"}`,
                  "&:hover": n.linkUrl ? { backgroundColor: "#e2e8f0" } : {},
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: n.isRead ? 500 : 700, flex: 1 }}>
                    {n.title}
                  </Typography>
                  <Chip
                    size="small"
                    label={n.level}
                    sx={{
                      backgroundColor: LEVEL_COLOR[n.level] || "#94a3b8",
                      color: "#fff",
                      height: 18,
                      fontSize: 10,
                      fontWeight: 700,
                    }}
                  />
                </Box>
                {n.body ? (
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                    {n.body}
                  </Typography>
                ) : null}
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                  {fmtAge(n.createdAtMs)}
                </Typography>
              </Box>
            ))}
          </Stack>
        )}
      </Popover>
    </>
  );
};

export default NotificationBell;
